/**
 * useChatStream — fetch + ReadableStream parser for the /chat SSE endpoint.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED §8.
 *
 * Why fetch + ReadableStream instead of EventSource:
 *   - EventSource is GET-only; we need POST for the message body.
 *   - EventSource also can't set custom headers, so it would be incompatible
 *     with the JWT bearer auth this app already uses.
 *
 * Event protocol (server → client, one JSON line per `data:` frame):
 *   {"type":"text_delta","text":"..."}
 *   {"type":"tool_result","tool_name":"...","result":{...}}
 *   {"type":"gap_q","field":"...","blocking":true,"prose":"...",...}
 *   {"type":"error","message":"..."}
 *   [DONE]
 *   [ERROR] <message>          ← legacy sentinel; new code emits "error" JSON
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { api } from '../../../api/client';
import { chatSessionKeys } from '../../../api/hooks/useChatSessions';
import { dealKeys } from '../../../api/hooks/useDeals';
import { useChatStore } from '../../../stores/chatStore';
import { useUIStore } from '../../../stores/uiStore';
import type { Citation } from '../types';

/** Authoritative deal/term-sheet context that accompanies a synthetic
 * resume turn. The backend treats `deal_id` here as ground truth for the
 * turn — overriding the body's stale `deal_id` if they disagree. This is
 * the defense-in-depth signal that prevents cross-deal leaks when the
 * frontend's `dealContext` pointer drifts (e.g. stale localStorage from
 * a previously-viewed deal). */
export interface SyntheticAction {
  kind:
    | 'deal_created'
    | 'term_sheet_uploaded'
    | 'deal_selected'
    | 'term_sheet_selected';
  deal_id: number;
  term_sheet_id?: number;
}

interface SendOptions {
  text: string;
  /** V1.4.1 — when true, the user-side message is sent to the backend so
   * the model has context, but is filtered out of the chat thread. Used
   * by UI control cards (deal creation, upload, deal/term-sheet
   * selection) for their synthetic resume messages. */
  synthetic?: boolean;
  /** Optional authoritative context for synthetic turns. Required on
   * deal-creation, upload-completion, and selection-confirmation paths
   * so the backend can self-heal a stale `dealContext.deal_id`. */
  syntheticAction?: SyntheticAction;
}

// Tier 2 tools that mutate the Deal record server-side. After each successful
// call, the deal-page TanStack queries are invalidated so the live mirror
// (visual-feedback addendum §5) sees the change without a manual refresh.
// V1.4: create_deal retired — deal creation runs through useCreateDeal
// inside ChatDealCreationCard, which already invalidates dealKeys via its
// own onSuccess callback. No model tool dispatches it anymore.
const TIER_2_DEAL_MUTATORS = new Set([
  'update_deal_notes',
  'mark_deal_status',
  'archive_deal',
  'update_deal_field',
]);

export function useChatStream() {
  const showToast = useUIStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(async ({ text, synthetic, syntheticAction }: SendOptions) => {
    const store = useChatStore.getState();
    if (!text.trim() || store.isStreaming) {return;}

    // Track the message for the Retry banner; cleared on [DONE].
    store.setLastUserMessage(text);
    // Sending a new turn supersedes any pending gap question, navigation
    // chip, upload card, deal-creation card, selection cards, or stale
    // error.
    store.clearGapQuestion();
    store.clearPendingNavigation();
    // Preserve an in-flight term-sheet upload: clear only the dropzone prompt,
    // NOT inflightUpload, so the progress card + extraction polling survive a
    // mid-upload message (the upload finishes and auto-advances on its own).
    store.setUploadPrompt(null);
    store.clearDealCreationPrompt();
    store.clearDealSelectionPrompt();
    store.clearTermSheetSelectionPrompt();
    store.clearError();

    // Push the user message immediately for snappy UX, then start streaming.
    store.appendMessage({ id: '', role: 'user', content: text, synthetic });
    // Seed an empty assistant turn so the streamer has somewhere to land deltas.
    store.appendMessage({ id: '', role: 'assistant', content: '' });
    // 'thinking' until the first text/tool delta arrives — drives the
    // skeleton indicator. Flips to 'streaming' on first text_delta below.
    store.setStreamingPhase('thinking');

    // Refresh the JWT proactively before opening the SSE stream — same
    // pattern as authStore.checkAuth(). The chat fetch bypasses the axios
    // interceptor that normally auto-refreshes, so a stale token here
    // would otherwise 401 the whole turn after the user has already
    // committed their message.
    if (api.token.isExpired()) {
      store.setStreamingPhase('idle');
      showToast('Your session has expired — please sign in again.', 'error');
      return;
    }
    if (api.token.isExpiringSoon()) {
      try {
        await api.auth.refresh();
      } catch {
        store.setStreamingPhase('idle');
        showToast('Could not refresh your session — please sign in again.', 'error');
        return;
      }
    }

    const token = api.token.get();
    if (!token) {
      store.setStreamingPhase('idle');
      showToast('You need to sign in to use chat.', 'error');
      return;
    }

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    // Live mirror — when a Tier 2 tool mutates a Deal, invalidate the
    // deal-page TanStack queries so any open deal view re-fetches and
    // reflects the change within ~1s.
    //
    // shouldReplaceNextText: when the model emits text before a tool call
    // ("I'll pull your data now…") and then emits MORE text after the tool
    // result, both blocks would normally append into a single content string
    // — causing the "repeating itself" effect. Setting this flag on each
    // tool result means the NEXT text block will replace (not append to)
    // whatever was typed before. Content never goes empty during streaming
    // (cards stay visible), and only the final synthesis sentence remains.
    let shouldReplaceNextText = false;

    const onToolResult = (toolName: string, result: unknown) => {
      shouldReplaceNextText = true;

      if (!TIER_2_DEAL_MUTATORS.has(toolName)) {return;}
      const r = (result ?? {}) as { deal_id?: number; is_error?: boolean };
      if (r.is_error) {return;}
      if (typeof r.deal_id === 'number') {
        queryClient.invalidateQueries({ queryKey: dealKeys.detail(r.deal_id) });
      }
      // Lists pick up the [STATUS: ...] prefix and updated deal_notes too.
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    };

    // Typewriter smoothing buffer. SSE deltas land in chunks of varying
    // size (sometimes whole sentences); without smoothing the user sees big
    // blobs pop in. We queue incoming text and drip it into the store at a
    // steady rate so it reads as typing. Drip rate adapts to the buffer
    // size so we don't fall behind when the model bursts.
    const textBuffer: string[] = [];
    let streamComplete = false;
    let drainHandle: number | null = null;
    // When true, the next drain tick atomically REPLACES message.content
    // rather than appending — avoids a 2-frame empty-content flash between
    // the clearLastAssistantContent() call and the first appended character.
    let pendingContentReplace = false;

    const drainTick = () => {
      if (textBuffer.length === 0) {
        if (streamComplete && drainHandle != null) {
          window.clearInterval(drainHandle);
          drainHandle = null;
        }
        return;
      }
      const totalChars = textBuffer.reduce((s, c) => s + c.length, 0);
      // Base drip ~3 chars per 30ms tick (~100 cps). When backlog grows past
      // ~60 chars, accelerate so we catch up over ~20 ticks (~600ms) instead
      // of typing forever after the model already finished.
      const dripChars = Math.max(3, Math.ceil(totalChars / 20));
      let remaining = dripChars;
      let drip = '';
      while (remaining > 0 && textBuffer.length > 0) {
        const head = textBuffer[0];
        if (head.length <= remaining) {
          drip += head;
          textBuffer.shift();
          remaining -= head.length;
        } else {
          drip += head.slice(0, remaining);
          textBuffer[0] = head.slice(remaining);
          remaining = 0;
        }
      }
      if (drip) {
        if (pendingContentReplace) {
          pendingContentReplace = false;
          useChatStore.getState().replaceLastAssistantContent(drip);
        } else {
          useChatStore.getState().appendDeltaToLastAssistant(drip);
        }
      }
    };

    const onText = (text: string) => {
      // If a tool result arrived since the last text block, this is a new
      // text block that should replace the previous preamble — not append.
      // Stop the drain, flush the buffer, and arm pendingContentReplace so
      // the next drainTick calls replaceLastAssistantContent atomically.
      // Content goes old-preamble → new-text in one React render cycle,
      // never passes through empty → showCards stays true, no card flash.
      if (shouldReplaceNextText) {
        shouldReplaceNextText = false;
        stopDrain();
        textBuffer.length = 0;
        pendingContentReplace = true;
      }
      // First delta of the turn — transition the skeleton out.
      if (useChatStore.getState().streamingPhase === 'thinking') {
        useChatStore.getState().setStreamingPhase('streaming');
      }
      textBuffer.push(text);
      if (drainHandle == null) {
        drainHandle = window.setInterval(drainTick, 30);
      }
    };

    const stopDrain = () => {
      if (drainHandle != null) {
        window.clearInterval(drainHandle);
        drainHandle = null;
      }
    };

    // Surface stopDrain on the controller so cancel() can flush it too.
    (controller as AbortController & { __stopDrain?: () => void }).__stopDrain = stopDrain;

    try {
      const resp = await fetch('/chat', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          messages: useChatStore
            .getState()
            .messages.filter((m) => m.role === 'user' || m.content)
            .map((m) => ({ role: m.role, content: m.content })),
          deal_id: useChatStore.getState().dealContext?.deal_id ?? null,
          // HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_F — advisor client scope.
          // Forwarded to chat_service which re-verifies ownership and injects
          // an advisor block into <session_context>. Null for SMEs / when no
          // client is active. client_name is non-authoritative — backend
          // re-fetches the canonical name from the DB after ownership check.
          client_id: useChatStore.getState().advisorContext?.client_id ?? null,
          client_name: useChatStore.getState().advisorContext?.client_name ?? null,
          // PHASE_E telemetry — backend auto-creates a session if NULL
          // and emits a `session_assigned` SSE event we'll store on the
          // first frame.
          session_id: useChatStore.getState().sessionId,
          // Synthetic turns (deal-creation, upload, selection card resume
          // messages) are forwarded to the model for context but must not
          // be persisted to chat_messages — they contain raw internal data
          // that would leak as a user bubble on session restore/refresh.
          synthetic_turn: synthetic ?? false,
          // Authoritative deal context for synthetic turns. Backend prefers
          // this over `deal_id` above and logs a structured warning on
          // mismatch — defense in depth against a stale `dealContext`
          // pointer leaking another deal's data into the response.
          synthetic_action: syntheticAction ?? null,
        }),
      });

      if (!resp.ok || !resp.body) {
        let detail = '';
        try {
          detail = await resp.text();
        } catch {
          // ignore
        }
        const status = resp.status;
        if (status === 403) {
          showToast('Chat is not enabled for your account yet.', 'warning');
        } else {
          showToast(`Chat request failed (${status})${detail ? `: ${detail.slice(0, 200)}` : ''}`, 'error');
          // Auth failures (401) are pointless to retry; everything else
          // gets a Retry banner.
          if (status !== 401) {
            useChatStore.getState().setError(`Chat request failed (${status})`);
          }
        }
        useChatStore.getState().setStreamingPhase('idle');
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // SSE frames are separated by \n\n. We accumulate bytes and split on
      // that boundary, leaving any partial frame in the buffer.
      while (true) {
        const { value, done } = await reader.read();
        if (done) {break;}
        buffer += decoder.decode(value, { stream: true });

        let sep: number;
         
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          handleFrame(frame, onToolResult, onText);
        }
      }
      // Drain any trailing single frame (no terminator).
      if (buffer.trim()) {handleFrame(buffer, onToolResult, onText);}
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        // Intentional abort — no toast, no Retry banner.
      } else {
        const message = (e as Error).message || 'unknown error';
        showToast(`Chat error: ${message}`, 'error');
        useChatStore.getState().setError(message);
      }
    } finally {
      // Mark the stream complete so the drain finishes the queue and stops.
      // streamingPhase stays non-idle while characters are still being typed
      // out, then flips to 'idle' once the buffer drains. Clearing
      // lastUserMessage is gated on no-error so the Retry banner survives.
      streamComplete = true;
      const finishStreamingWhenIdle = window.setInterval(() => {
        if (drainHandle == null) {
          window.clearInterval(finishStreamingWhenIdle);
          const state = useChatStore.getState();
          state.setStreamingPhase('idle');
          if (state.error == null) {
            state.setLastUserMessage(null);
          }
        }
      }, 50);
      // Refresh the recent-chats sidebar. After commit 196bd79 the backend
      // hides sessions with zero persisted messages, so a freshly created
      // session only appears once its first user+assistant pair has been
      // written. Invalidating here also bumps existing sessions to the top
      // by lastMessageAt.
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() });
      abortRef.current = null;
    }
  }, [showToast, queryClient]);

  const cancel = useCallback(() => {
    const ctrl = abortRef.current as
      | (AbortController & { __stopDrain?: () => void })
      | null;
    ctrl?.__stopDrain?.();
    ctrl?.abort();
  }, []);

  return { send, cancel };
}

/** Process a single SSE frame. */
function handleFrame(
  rawFrame: string,
  onToolResult?: (toolName: string, result: unknown) => void,
  onText?: (text: string) => void,
) {
  const store = useChatStore.getState();
  for (const line of rawFrame.split('\n')) {
    if (!line.startsWith('data:')) {continue;}
    const payload = line.slice(5).trim();
    if (!payload) {continue;}

    if (payload === '[DONE]') {
      // Streaming finished naturally — drop the lastUserMessage so the
      // Retry banner does not linger after a successful turn.
      if (store.error == null) {store.setLastUserMessage(null);}
      return;
    }
    if (payload.startsWith('[ERROR]')) {
      // Legacy sentinel — kept for back-compat. New code emits
      // {"type":"error","message":"..."} JSON instead.
      const message = payload.replace(/^\[ERROR\]\s*/, '');
      store.setError(message || 'An unexpected error occurred.');
      return;
    }
    try {
      const json = JSON.parse(payload) as {
        type?: string;
        text?: string;
        tool_name?: string;
        result?: unknown;
        message?: string;
        // gap_q fields
        field?: string;
        suggestion?: string;
        source?: string;
        blocking?: boolean;
        prose?: string;
        // session_assigned field
        session_id?: string;
        // navigate_to fields (V1.1 Item 6)
        destination?: string;
        label?: string;
        deal_id?: number;
        reason?: string;
        // Citations Phase 2 — citation event payload
        block_index?: number;
        citation?: unknown;
      };
      if (json.type === 'text_delta' && typeof json.text === 'string') {
        // Route text through the smoothing buffer so it types out at a
        // steady cadence instead of dropping in chunks.
        if (onText) {onText(json.text);}
        else {store.appendDeltaToLastAssistant(json.text);}
      } else if (json.type === 'tool_result' && json.tool_name) {
        // Tool results render their cards instantly; not buffered.
        store.appendToolResultToLastAssistant({ tool_name: json.tool_name, result: json.result });
        onToolResult?.(json.tool_name, json.result);
      } else if (json.type === 'gap_q' && typeof json.field === 'string') {
        // Backend has paused the agent loop and wants the user to
        // confirm/correct a missing field. Render the gap chip; the
        // user's next message resumes the original question.
        store.setGapQuestion({
          field: json.field,
          suggestion: typeof json.suggestion === 'string' && json.suggestion ? json.suggestion : null,
          source: typeof json.source === 'string' && json.source ? json.source : null,
          blocking: Boolean(json.blocking),
          prose: typeof json.prose === 'string' ? json.prose : '',
        });
      } else if (
        json.type === 'navigate_to' &&
        typeof json.destination === 'string'
      ) {
        // V1.1 Item 6 — UI control tool. Backend continues without pausing;
        // we just append a chip below the assistant prose. V1.3 retired
        // 'upload' and 'deal_upload' destinations — uploads now flow
        // through the inline ChatUploadCard.
        store.setPendingNavigation({
          destination: json.destination as
            | 'deals_list'
            | 'deal_detail'
            | 'analysis',
          label: typeof json.label === 'string' ? json.label : 'Go',
          deal_id: typeof json.deal_id === 'number' ? json.deal_id : undefined,
          reason: typeof json.reason === 'string' ? json.reason : undefined,
        });
      } else if (
        json.type === 'upload_prompt' &&
        typeof json.deal_id === 'number'
      ) {
        // V1.3 — UI control tool, terminates the agent loop. Render an
        // inline file-picker card. ChatUploadCard handles the upload and
        // injects a synthetic user turn on success to resume the agent.
        // Swallow a redundant re-prompt while an upload is already in flight:
        // the progress card stays mounted and extraction auto-advances.
        if (!useChatStore.getState().inflightUpload) {
          store.setUploadPrompt({
            deal_id: json.deal_id,
            label:
              typeof json.label === 'string' && json.label
                ? json.label
                : 'Upload your term sheet',
            reason: typeof json.reason === 'string' ? json.reason : undefined,
          });
        }
      } else if (json.type === 'deal_selection_prompt') {
        // V1.5 — UI control tool, terminates the agent loop. Render the
        // ChatDealSelectionCard with the user's deals. Clicking a deal
        // sets dealContext and resumes the agent with a synthetic
        // confirmation message.
        const rawDeals = (json as Record<string, unknown>).deals;
        const dealsArray = Array.isArray(rawDeals) ? rawDeals : [];
        store.setDealSelectionPrompt({
          deals: dealsArray as never,
          reason:
            typeof (json as { reason?: unknown }).reason === 'string'
              ? ((json as { reason?: string }).reason)
              : undefined,
        });
      } else if (json.type === 'term_sheet_selection_prompt') {
        // V1.5 — UI control tool, terminates the agent loop. Render the
        // ChatTermSheetSelectionCard for a deal with multiple analyzed
        // term sheets.
        const rawTs = (json as Record<string, unknown>).term_sheets;
        const tsArray = Array.isArray(rawTs) ? rawTs : [];
        const dealIdRaw = (json as { deal_id?: unknown }).deal_id;
        store.setTermSheetSelectionPrompt({
          deal_id: typeof dealIdRaw === 'number' ? dealIdRaw : 0,
          term_sheets: tsArray as never,
          reason:
            typeof (json as { reason?: unknown }).reason === 'string'
              ? ((json as { reason?: string }).reason)
              : undefined,
        });
      } else if (json.type === 'deal_creation_prompt') {
        // V1.4 — UI control tool, terminates the agent loop. Render the
        // ChatDealCreationCard with model-extracted prefill. Card collects
        // required fields and POSTs /deals directly; on success it injects
        // a synthetic user turn that resumes the conversation.
        const rawPrefill = (json as Record<string, unknown>).prefill;
        const prefill =
          rawPrefill && typeof rawPrefill === 'object'
            ? (rawPrefill as Record<string, unknown>)
            : {};
        store.setDealCreationPrompt({
          prefill: {
            deal_name:
              typeof prefill.deal_name === 'string' ? prefill.deal_name : undefined,
            financing_purpose:
              typeof prefill.financing_purpose === 'string'
                ? prefill.financing_purpose
                : undefined,
            financing_amount:
              typeof prefill.financing_amount === 'number'
                ? prefill.financing_amount
                : undefined,
            industry:
              typeof prefill.industry === 'string' ? prefill.industry : undefined,
            timeline_urgency:
              typeof prefill.timeline_urgency === 'string'
                ? prefill.timeline_urgency
                : undefined,
          },
        });
      } else if (
        json.type === 'citation' &&
        typeof json.block_index === 'number' &&
        json.citation
      ) {
        // Citations Phase 2 — citation delta from the SDK streaming
        // accumulator. Append to the parallel metadata array on the
        // current assistant message; DO NOT route through the
        // typewriter buffer (citations are metadata, not text).
        store.appendCitationToLastAssistant({
          block_index: json.block_index,
          citation: json.citation as Citation,
        });
      } else if (json.type === 'error') {
        // Structured error envelope — generic message only (no stack
        // traces / Pydantic internals leak through).
        store.setError(typeof json.message === 'string' && json.message
          ? json.message
          : 'An unexpected error occurred.');
      } else if (json.type === 'session_assigned' && typeof json.session_id === 'string') {
        // PHASE_E telemetry — backend auto-created a session for this
        // turn (because we sent session_id: null). Store it so subsequent
        // turns reuse the same session, and the useChatTelemetry hook
        // has a session to attribute frontend events to.
        store.setSessionId(json.session_id);
      }
    } catch {
      // Malformed JSON — skip the frame rather than tear down the stream.
    }
  }
}
